import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import { useDispatch, useSelector } from "react-redux";
import { setSpinComplete } from "../../../State/Slices/spinSlice";
import { playSound } from "../../../State/Slices/soundsSlice";
import { Symbol } from "../../../Elements/Symbol/Symbol";
import { symbolData } from "../../../SymbolData";
import FreeSpinReelController from "./FreeSpinReelController";

const FreeSpinReel = forwardRef(({ reel, index, symbolSize }, ref) => {
  const freeSpinReelController = useRef(null);
  const dispatch = useDispatch();

  // ✅ Use `currentRound` instead of `round[roundIndex]`
  const { currentRound } = useSelector((state) => state.game);

  const symbolsContainerRef = useRef(null);
  const symbolsSpriteSheet = PIXI.Assets.get(ASSET_NAME.symbols);

  const symbolsSpineData = (() => {
    const spineReferences = {};

    for (let i = 1; i <= 13; i++) {
      const symbolKey = `sym${String(i).padStart(2, "0")}`;
      const assetName = ASSET_NAME[symbolKey];
      const spineData = PIXI.Assets.get(assetName);

      if (!spineData || !spineData.spineData) {
        console.warn(`Failed to load Spine data for symbol: ${symbolKey}`);
        continue;
      }

      spineReferences[symbolKey] = spineData.spineData; // Cache only SkeletonData
    }
    return spineReferences;
  })();

  const handleReelSpinComplete = useCallback(() => {
    dispatch(setSpinComplete(true));
  }, [dispatch]);

  const handlePlaySound = useCallback(
    (soundName) => {
      dispatch(playSound(soundName));
    },
    [dispatch]
  );

  useEffect(() => {
    if (
      symbolsContainerRef.current &&
      !freeSpinReelController.current &&
      symbolsSpriteSheet
    ) {
      freeSpinReelController.current = new FreeSpinReelController(
        symbolsContainerRef.current,
        symbolsSpriteSheet,
        symbolsSpineData,
        currentRound?.reels, // ✅ Updated
        index,
        symbolSize,
        handleReelSpinComplete,
        handlePlaySound
      );
    }
  }, [
    index,
    symbolsSpriteSheet,
    symbolsSpineData,
    currentRound,
    symbolSize,
    handleReelSpinComplete,
    handlePlaySound,
  ]);

  useEffect(() => {
    if (freeSpinReelController.current) {
      freeSpinReelController.current.updateReels(
        currentRound?.reels?.[index] // ✅ Updated
      );
    }
  }, [currentRound, index]);

  useEffect(() => {
    if (freeSpinReelController.current) {
      freeSpinReelController.current.resizeSymbol(symbolSize);
    }
  }, [symbolSize]);

  useImperativeHandle(ref, () => ({
    startFreeSpin: (spinTime, onComplete) => {
      freeSpinReelController.current?.spin(spinTime, onComplete);
    },
    stopFreeSpin: () => {
      freeSpinReelController.current?.stopSpin();
    },
    getReelController: () => {
      return freeSpinReelController.current;
    },
  }));

  return (
    <Container
      ref={symbolsContainerRef}
      name="SymbolsContainer"
      zIndex={2}
      sortableChildren
      y={0}
      x={0}
    >
      {reel.map((symbol, idx) => {
        const texture =
          symbolsSpriteSheet.textures[`${symbolData[symbol].name}.png`];
        const spineData = symbolsSpineData[symbolData[symbol].name];

        return (
          <Symbol
            key={idx}
            symbolSize={symbolSize}
            index={idx}
            texture={texture}
            spineData={spineData}
            showSpine={0}
          />
        );
      })}
    </Container>
  );
});

export default FreeSpinReel;
